/**
 * Cookie & tracking-consent state management.
 *
 * Design goals
 * ------------
 * 1. Comply with GDPR / UK-GDPR / ePrivacy by defaulting every non-essential
 *    tracker to "denied" until the visitor makes an affirmative choice.
 * 2. Support Google Consent Mode v2 (`ad_storage`, `analytics_storage`,
 *    `ad_user_data`, `ad_personalization`) so GA4 + Google Ads gets
 *    granular signals instead of a hard block.
 * 3. Gate Meta Pixel firing so it only initializes once marketing consent
 *    is granted (see `lib/metaPixel.js`).
 * 4. Persist choice in `localStorage` so returning visitors are not
 *    re-prompted; version-key it so we can force-invalidate stored consent
 *    if we ever add a new tracker category.
 */
import { devLog } from "@/lib/devLog";

const STORAGE_KEY = "dc_consent_v1";

// Canonical shape of the consent record we persist. Keys map 1-to-1 to
// Google Consent Mode v2 signal names so we can dispatch straight through.
export const CATEGORY_NECESSARY  = "necessary";
export const CATEGORY_ANALYTICS  = "analytics";
export const CATEGORY_MARKETING  = "marketing";

export const DEFAULT_CONSENT = Object.freeze({
  [CATEGORY_NECESSARY]: true,   // always on — required for the site to work
  [CATEGORY_ANALYTICS]: false,
  [CATEGORY_MARKETING]: false,
  decided: false,               // true only after user clicks Accept/Reject/Save
  timestamp: null,
});

// -------------- Storage helpers --------------
export const loadConsent = () => {
  if (typeof window === "undefined") return { ...DEFAULT_CONSENT };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_CONSENT };
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_CONSENT, ...parsed, decided: true };
  } catch (err) {
    devLog.warn("consent: failed to read stored consent", err);
    return { ...DEFAULT_CONSENT };
  }
};

export const saveConsent = (partial) => {
  const merged = {
    ...loadConsent(),
    ...partial,
    decided: true,
    timestamp: new Date().toISOString(),
  };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  } catch (err) {
    devLog.warn("consent: failed to persist consent", err);
  }
  applyToDataLayer(merged);
  broadcast(merged);
  return merged;
};

// -------------- Google Consent Mode v2 --------------
const pushToDataLayer = (event) => {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(event);
};

/**
 * Emit the initial "denied" default BEFORE any Google tag fires. This is
 * required for Consent Mode to work in advanced mode. Call once as early
 * as possible in bootstrap.
 */
export const initConsentDefaults = () => {
  pushToDataLayer(["consent", "default", {
    ad_storage:            "denied",
    ad_user_data:          "denied",
    ad_personalization:    "denied",
    analytics_storage:     "denied",
    functionality_storage: "granted",
    security_storage:      "granted",
    wait_for_update:       500,
  }]);
};

/**
 * Dispatch a consent update after the user chooses. Called from `saveConsent`
 * so it runs automatically.
 */
export const applyToDataLayer = (consent) => {
  const state = {
    ad_storage:            consent[CATEGORY_MARKETING]  ? "granted" : "denied",
    ad_user_data:          consent[CATEGORY_MARKETING]  ? "granted" : "denied",
    ad_personalization:    consent[CATEGORY_MARKETING]  ? "granted" : "denied",
    analytics_storage:     consent[CATEGORY_ANALYTICS]  ? "granted" : "denied",
    functionality_storage: "granted",
    security_storage:      "granted",
  };
  pushToDataLayer(["consent", "update", state]);
};

// -------------- In-app subscribers --------------
const subscribers = new Set();

export const subscribeConsent = (fn) => {
  subscribers.add(fn);
  return () => subscribers.delete(fn);
};

const broadcast = (consent) => {
  subscribers.forEach((fn) => {
    try {
      fn(consent);
    } catch (err) {
      devLog.warn("consent: subscriber threw", err);
    }
  });
};

// -------------- Convenience --------------
export const hasMarketingConsent = () => loadConsent()[CATEGORY_MARKETING] === true;
export const hasAnalyticsConsent = () => loadConsent()[CATEGORY_ANALYTICS] === true;
export const hasDecided = () => loadConsent().decided === true;

export const acceptAll = () => saveConsent({
  [CATEGORY_ANALYTICS]: true,
  [CATEGORY_MARKETING]: true,
});
export const rejectAll = () => saveConsent({
  [CATEGORY_ANALYTICS]: false,
  [CATEGORY_MARKETING]: false,
});
